import {
  ActionButton,
  ActionsGroup,
  BasicInput,
  Col,
  Entidade,
  FormikSelect,
  PagedResponse,
  Row,
  SectionTitle,
  Table,
  Yup,
  useShowNotification
} from '@elotech/components';
import { AxiosPromise } from 'axios';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { FormGroup } from 'react-bootstrap';

import {
  ProtocoloArquivoDTO,
  ProtocoloBaseDTO,
  ProtocoloDTO
} from '../../../../type';
import { OrigemProtocoloEnum } from '../../../../type/enum';
import { enumToOptions } from '../../../../utils';
import { InlineButton } from '../../../inline-button/InlineButton';
import { TableContainer } from '../../../table';
import { AssinaturaQuickView } from '../../AssinaturaQuickView';
import { ProtocoloContainerPermissions } from '../../ProtocoloContainerBase';
import { ProtocoloSelect } from './ProtocoloSelect';

type Props = {
  protocolos?: ProtocoloBaseDTO[];
  onAddProtocolo: (dto: ProtocoloBaseDTO) => void;
  onMarcarPrincipal: (id: string) => void;
  onPreviewArquivo: (fullPath: string, fileName: string) => void;
  onRemove: (id: string) => void;
  onEntidadeSearch?: (
    search: string,
    pagination?: any,
    sort?: any
  ) => AxiosPromise<PagedResponse<Entidade>>;
  permissions: ProtocoloContainerPermissions;
};

export const ProtocoloComponent = ({
  protocolos = [],
  onAddProtocolo,
  onMarcarPrincipal,
  onPreviewArquivo,
  onRemove,
  onEntidadeSearch,
  permissions
}: Props) => {
  const [arquivoQuick, setArquivoQuick] = useState<ProtocoloArquivoDTO>();

  const showNotification = useShowNotification();

  const processosInternos = protocolos?.filter(p => p.protocolo?.id);
  const processosExternos = protocolos?.filter(p => !p.protocolo?.id);

  const { create: canAdd, update: canUpdate, delete: canDelete } = permissions;
  const exibirEntidade = !!onEntidadeSearch;

  const onViewProtocolo = (protocolo: ProtocoloDTO) => {
    const { hostname, origin } = window.location;
    const isLocalhost = hostname.includes('localhost');
    const url = `${origin}/protocolo/processos-novo/view/?tipo=${protocolo.tipo?.id}&numero=${protocolo.numero}&ano=${protocolo.ano}&entidade=${protocolo.entidade?.id}`;

    if (isLocalhost) {
      return showNotification({
        level: 'warning',
        message: 'Não é possível abrir o link como localhost'
      });
    }

    if (!window.open(url, '_blank')) {
      throw new Error('Sem permissão para mostrar popups');
    }
  };

  const onPreview = (protocolo: ProtocoloDTO) => {
    if (protocolo?.arquivos?.length) {
      const arquivo = protocolo?.arquivos[0];
      onPreviewArquivo(arquivo.urlS3, arquivo.nome);
    }
  };

  const onViewAssinatura = (protocolo: ProtocoloDTO) => {
    if (protocolo?.arquivos?.length) {
      setArquivoQuick(protocolo?.arquivos[0]);
    }
  };

  const showEntidadeColumn = () => {
    return onEntidadeSearch || exibirEntidade ? (
      <Table.Column
        name="entidade"
        header="Entidade"
        value={(protocolo: ProtocoloDTO) => {
          return `${protocolo.entidade?.id} - ${protocolo.entidade?.nome}`;
        }}
      />
    ) : undefined;
  };

  const renderAddProtocolo = () => {
    if (!canAdd) return;

    const addedIds = (protocolos?.map(p => p.protocolo?.uuid).filter(Boolean) ||
      []) as string[];

    return (
      <>
        <SectionTitle>Pesquisar Protocolos</SectionTitle>
        <ProtocoloSelect
          onAddProtocolo={protocolo =>
            onAddProtocolo({
              origem: OrigemProtocoloEnum.INTERNO,
              anoIdentificacao: protocolo.ano,
              descricao: protocolo.descricao,
              link: protocolo.link,
              numeroIdentificacao: protocolo.numero,
              protocoloId: protocolo.uuid
            } as ProtocoloBaseDTO)
          }
          addedIds={addedIds}
          onEntidadeSearch={onEntidadeSearch}
        />
      </>
    );
  };
  const renderAddProcessoExterno = () =>
    canAdd && (
      <Formik
        onSubmit={(values, { resetForm }) => {
          onAddProtocolo({
            origem: values.origem,
            descricao: values.descricao,
            numeroIdentificacao: values.numero,
            anoIdentificacao: values.ano,
            link: values.link
          } as ProtocoloBaseDTO);
          resetForm();
        }}
        initialValues={{
          origem: OrigemProtocoloEnum.SEI,
          descricao: '',
          numero: '',
          ano: undefined,
          link: ''
        }}
        validationSchema={Yup.object().shape({
          origem: Yup.string().required('Origem é obrigatório'),
          numero: Yup.string().required('Número é obrigatório')
        })}
      >
        {formProps => (
          <Form>
            <Row>
              <FormikSelect
                name="origem"
                label="Origem"
                getOptionValue={origem => origem.value}
                getOptionLabel={origem => origem.description}
                options={enumToOptions(OrigemProtocoloEnum).filter(
                  origem => origem.value !== OrigemProtocoloEnum.PROCESSODIGITAL
                )}
                size={2}
              />
              <BasicInput
                size={2}
                name="numero"
                label="Número"
                maxLength={100}
              />
              <BasicInput size={3} name="link" label="Link" maxLength={500} />
              <Col sm={2} md={2}>
                <FormGroup>
                  <InlineButton
                    className="mt-xs"
                    label="Adicionar"
                    onClick={formProps.submitForm}
                    icon="fa fa-plus"
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    );

  return (
    <>
      {renderAddProtocolo()}
      <SectionTitle>Protocolos Vinculados</SectionTitle>
      <TableContainer>
        <Table values={processosInternos}>
          {showEntidadeColumn()}
          <Table.Column
            name="numero"
            header="Número"
            value={({ protocolo }: ProtocoloBaseDTO) => protocolo?.numero}
          />
          <Table.Column
            name="ano"
            header="Ano"
            value={({ protocolo }: ProtocoloBaseDTO) => protocolo?.ano}
          />
          <Table.Column
            name="situacao"
            header="Situação"
            value={({ protocolo }: ProtocoloBaseDTO) =>
              protocolo?.situacao.descricao
            }
          />
          <Table.Column
            name="requerente"
            header="Requerente"
            value={({ protocolo }: ProtocoloBaseDTO) => protocolo?.pessoa?.nome}
          />
          <Table.Column
            name="assunto"
            header="Assunto"
            value={({ protocolo }: ProtocoloBaseDTO) =>
              `${protocolo?.assunto?.id} - ${protocolo?.assunto?.descricao}`
            }
          />
          <Table.Column
            name="tipo"
            header="Tipo"
            value={({ protocolo }: ProtocoloBaseDTO) =>
              `${protocolo?.tipo?.id} - ${protocolo?.tipo?.descricao}`
            }
          />
          <Table.Column
            name="observacao"
            header="Observação"
            value={({ protocolo }: ProtocoloBaseDTO) => protocolo?.observacao}
          />
          <Table.Column
            name="principal"
            header="Principal"
            value={(protocolo: ProtocoloBaseDTO) =>
              protocolo.principal ? 'Sim' : 'Não'
            }
          />
          <Table.Column
            header=""
            value={(protocolo: ProtocoloBaseDTO) => {
              return (
                <ActionsGroup>
                  {!protocolo.principal! && canUpdate && (
                    <ActionButton
                      label="Marcar como principal"
                      icon="check"
                      onClick={() => onMarcarPrincipal(protocolo.id || '')}
                    />
                  )}
                  <ActionButton
                    label="Visualizar Processo"
                    icon="eye"
                    onClick={() => onViewProtocolo(protocolo.protocolo!)}
                  />
                  <ActionButton
                    label="Visualizar Arquivo"
                    icon="file"
                    onClick={() => onPreview(protocolo.protocolo!)}
                  />
                  <ActionButton
                    icon="file-signature"
                    label="Assinaturas"
                    onClick={() => onViewAssinatura(protocolo.protocolo!)}
                  />
                  {canDelete && (
                    <ActionButton
                      label="Excluir"
                      icon="trash"
                      onClick={() => onRemove(protocolo.id!)}
                    />
                  )}
                </ActionsGroup>
              );
            }}
          />
        </Table>
      </TableContainer>
      <SectionTitle>Processos Externos</SectionTitle>
      {renderAddProcessoExterno()}
      <TableContainer>
        <Table values={processosExternos}>
          <Table.Column
            name="origem"
            header="Origem"
            value={(protocolo: ProtocoloBaseDTO) => protocolo.origem}
          />
          <Table.Column
            name="numero"
            header="Número"
            value={(protocolo: ProtocoloBaseDTO) =>
              protocolo.numeroIdentificacao
            }
          />
          <Table.Column
            name="link"
            header="Link"
            value={(protocolo: ProtocoloBaseDTO) => protocolo.link}
          />
          <Table.Column
            name="principal"
            header="Principal"
            value={(protocolo: ProtocoloBaseDTO) =>
              protocolo.principal ? 'Sim' : 'Não'
            }
          />
          <Table.Column
            header=""
            value={(protocolo: ProtocoloBaseDTO) => {
              return (
                <ActionsGroup>
                  {!protocolo.principal! && canAdd && (
                    <ActionButton
                      label="Marcar como principal"
                      icon="check"
                      onClick={() => onMarcarPrincipal(protocolo.id! || '')}
                    />
                  )}
                  {canDelete && (
                    <ActionButton
                      label="Excluir"
                      icon="trash"
                      onClick={() => onRemove(protocolo.id!)}
                    />
                  )}
                </ActionsGroup>
              );
            }}
          />
        </Table>
      </TableContainer>
      {arquivoQuick && (
        <AssinaturaQuickView
          arquivo={arquivoQuick}
          loading={false}
          onClose={() => setArquivoQuick(undefined)}
        />
      )}
    </>
  );
};
